import Field from "@/types/form/Field";
import FieldRow from "@/types/form/FieldRow";
import FieldRule from "@/types/form/FieldRule";
import Form from "@/types/form/Form";
import FormCategory from "@/types/form/FormCategory";
import FormManager from "@/types/form/FormManager";
import FormRules from "@/types/form/FormRules";
import { formatHoursMinutes, formatDate, fileToBase64 } from "@/utils/helpers"
import useVuelidate from "@vuelidate/core";
import { required, email, minLength, sameAs, maxLength } from '@vuelidate/validators'
import { ref } from "vue";

/**
 * Checks if a row is a single field or a list of fields
 * @param {FieldRow} row the form row that needs to be checked
 * @returns {boolean} whether or not this row is a single field
 */
export const isSingleField = (row: FieldRow): boolean => {
    return typeof row === 'object' && !Array.isArray(row) && row !== null
}

/**
 * Finds a fieldName within a category
 * @param {FormCategory} category the category in which to find the field
 * @param {string} fieldName the fieldName to search
 * @returns {number[] | null} the path to a field from within a category
 */
export const categoryFindField = (category: FormCategory, fieldName: string) => {
    for (const [index1, row] of category.rows.entries()) {
        if (isSingleField(row)) {
            if (fieldName == (row as Field).name) {
                // -1 indicates that there is no second index
                // as this is a single value and not a list
                return [index1, -1]
            }
        } else {
            for (const [index2, field] of (row as Field[]).entries()) {
                if (fieldName == field.name) {
                    return [index1, index2]
                }
            }
        }
    }
    return null
}

export const extractFieldValue = (form, category = 0, row = 0, col = null) => {
    if (col !== null) return form.categories[category].rows[row][col].value
    return form.categories[category].rows[row].value
}

/**
 * Converts a form field to Vuelidate rules
 * @param {Field} field the field to convert to rules
 * @param {FormCategory} category the category of the field this is used as context
 * @returns {FieldRule} a field rule containing all validation rules for this field
 */
export const fieldToRules = (field: Field, category: FormCategory): FieldRule => {
    const fieldRule: FieldRule = {
        value: {
            $autoDirty: true,
        }
    }

    // Vuelidate email rule
    if (field.type == 'email') fieldRule.value.email = email

    // Vuelidate password verify sameAs rule
    if (field.mustBeSameAs) {
        const location = categoryFindField(category, field.mustBeSameAs)
        if (location) {
            if (location[1] == -1) {
                fieldRule.value.sameAsPassword = sameAs((category.rows[location[0]] as Field).value)
            } else {
                fieldRule.value.sameAsPassword = sameAs(category.rows[location[0]][location[1]].value)
            }
        }
    }

    // Vuelidate required rule
    if (field.required && !field.hidden) fieldRule.value.required = required

    // Vuelidate minLength rule
    if (field.minLength) fieldRule.value.minLength = minLength(field.minLength as number)
    if (field.exactLength) fieldRule.value.minLength = minLength(field.exactLength as number)
    if (field.type == 'auth-code') fieldRule.value.minLength = minLength(8)

    // Vuelidate maxLength rule
    if (field.maxLength) fieldRule.value.maxLength = maxLength(field.maxLength as number)
    if (field.exactLength) fieldRule.value.maxLength = maxLength(field.exactLength as number)
    if (field.type == 'auth-code') fieldRule.value.maxLength = maxLength(8)

    return fieldRule
}

/**
 * Converts a form to Vuelidate rules
 * @param {Form} form the form that needs to get validation rules
 * @returns {FormRules} the validation rules of the form
 */
export const formToRules = (form: Form): FormRules => {
    const formRules: FormRules = {
        categories: []
    }

    // for each category and each field in each row of the category
    // generate the Vuelidate rules
    if (!form || !form.categories) {
        console.warn("[forms.ts] formToRules: form or form.categories is undefined", form)
        return { categories: [] }
    }
    form.categories.forEach((category, index1) => {
        formRules.categories.push({
            rows: []
        })
        category.rows.forEach((row: any, index2) => {
            if (isSingleField(row)) {
                formRules.categories[index1].rows[index2] = fieldToRules(row, category)
            } else {
                formRules.categories[index1].rows[index2] = []
                row.forEach((field: Field) => {
                    (formRules.categories[index1].rows[index2] as FieldRule[]).push(fieldToRules(field, category))
                })
            }
        })
    })

    return formRules
}

/**
 * This formats a field value to a readable value for request bodies
 * @param {Field} field the field, used for context
 * @param value the new value of the field
 * @returns a formatted value
 */
const formatFieldValue = (field: Field, value: any) => {
    if (field.type == "date" && value != null) {
        value = formatDate(value)
    } else if (field.type == "time" && value != null) {
        value = formatHoursMinutes(value.hours, value.minutes)
    } else if (field.type == "iconSelect" && value != null) {
        try {
            value = field.options[value].id
        } catch (e) {
            value = null
        }
    } else if (field.type == "colors-limited" && value != null) {
        try {
            value = field.options[value]
        } catch (e) {
            value = null
        }
    } else if (field.type == "mpc" && value != null) {
        try {
            value = field.options[value].name
        } catch (e) {
            value = null
        }
    } else if (field.type == "radio" && value != null) {
        try {
            value = field.options[value]
        } catch (e) {
            value = null
        }
    }

    if (field.resultMap) {
        value = field.resultMap(value)
    }

    return value
}

/**
 * This function converts a form to a useable request body.
 * This is done based on the name of a field
 * Categories and rows are ignored and will not be shown in the request body
 * @param {Form} form 
 * @returns the request body to be sent to the backend
 */
export const formToRequestBody = (form: Form) => {
    const requestBody: any = {}

    forEachFieldInForm(form, (field) => {
        requestBody[field.name] = formatFieldValue(field, field.value)
    })

    return requestBody
}

/**
 * Sets the error fields of the fields of a form based on a response body
 * @param {Form} form The form on which to set the errors
 * @param errors the reponse body errors
 */
export const setFormErrors = (form: Form, errors) => {
    forEachFieldInForm(form, (field) => {
        field.errors = errors[field.name]
    })
}

/**
 * This function combines multiple forms into a single request body
 * @param {Form[]} forms the forms to be converted into a request body
 * @returns the request body to be sent to the backend
 */
export const formsToRequestBody = (forms: Form[]) => {
    let requestBody = {}
    forms.forEach(form => {
        requestBody = { ...requestBody, ...formToRequestBody(form) }
    })
    return requestBody
}

/**
 * Combines multiple forms into a single form
 * @param {FormManager[]} forms a list of forms to be combined
 * @param {string} newName the new name of the combined form
 * @returns {FormManager} the new combined form
 */
export const formsToForm = (forms: FormManager[], newName: string): FormManager => {
    return forms.reduce((acc, form) => {
        return useForm({
            name: newName,
            categories: acc.categories.concat(form.categories)
        })
    }, useForm({
        name: newName,
        categories: []
    })) as FormManager
}

/**
 * Executes a function for each field in a form
 * @param {FormManager} form the form for which to execute the function
 * @param fn the function to be executed for each field
 */
export const forEachFieldInForm = (form: Form, fn) => {
    form.categories.forEach(category => {
        category.rows.forEach(row => {
            if (isSingleField(row)) {
                const field = row as Field
                fn(field)
            } else {
                (row as Field[]).forEach(field => {
                    fn(field)
                })
            }
        })
    })
}

/**
 * Resets a form
 * @param {Form} form the form that needs to be reset
 * @returns {Form} the reset version of the form
 */
export const resetForm = (form: Form): Form => {
    forEachFieldInForm(form, (field: Field) => {
        field.value = field.default !== undefined ? field.default : (field.multiple ? [] : null)
    })
    return form
}

/**
 * Clears the error fields of a form
 * @param {Form} form the form of which the errors must be cleared
 * @returns {Form} the form where the errors have been cleared
 */
export const clearErrors = (form: Form): Form => {
    forEachFieldInForm(form, (field: Field) => {
        field.errors = null
    })
    return form
}

/**
 * Updates a validator
 * @param form the form on which to base the validation rules
 * @param validator the validator to be reset
 */
export const updateValidator = (form, validator) => {
    let formRules = formToRules(form.value)
    let v$ = useVuelidate(formRules, form)
    validator.value = v$
}

/**
 * Gets the first form that contains a response error
 * @param {FormManager[]} forms the list of forms for which we must check
 * @param errors the response errors
 * @returns {number} the index of the first form that contained an error
 */
export const getFirstFormError = (forms: FormManager[], errors): number => {
    try {
        let firstFormWithError = -1
        for (const [index, form] of forms.entries()) {
            forEachFieldInForm(form, (field) => {
                if (errors[field.name] != null) {
                    if (firstFormWithError == -1) firstFormWithError = index
                }
            })
        }
        return firstFormWithError
    } catch (e) {
        return -1
    }
}

/**
 * Deep clones an object
 * @param obj the object to be cloned
 * @returns the cloned object
 */
export const deepClone = (obj: any): any => {
    if (obj === null || typeof obj !== 'object') return obj
    if (Array.isArray(obj)) return obj.map(deepClone)
    const cloned = {}
    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            cloned[key] = deepClone(obj[key])
        }
    }
    return cloned
}

/**
 * Adds helper functions to the form object
 * @param {Form} form the form to be converted to a FormManager
 * @returns {FormManager} the managed form object
 */
export const useForm = (form: Form): FormManager => {
    form = deepClone(form)
    form = resetForm(form)
    const formManager: FormManager = {
        ...form,
        form: ref(form),
        reset() {
            form = resetForm(form)
        },
        toRequestBody() {
            return formToRequestBody(form)
        },
        clearErrors() {
            this.showErrors = false
            clearErrors(this)
        },
        setErrors(errors) {
            this.showErrors = true
            setFormErrors(this, errors)
        },
        updateValidator(form, validator) {
            return updateValidator(form, validator)
        }
    }
    return formManager
}

/**
 * converts a list of forms to a list of FormManagers
 * @param {Form[]} forms the forms to be converted
 * @returns {FormManager[]} a list of form managers
 */
export const useForms = (forms: Form[]): FormManager[] => {
    return forms.map(form => useForm(form))
}